import React, { useEffect, useState } from 'react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

/**
 * 2026-02 — Business Contacts whitelist (Settings → Business Contacts).
 *
 * Manages the agent's whitelist of business / administrative email
 * senders. Whitelisted senders bypass lead triage entirely and land in
 * the dashboard's "Business Emails" section as notifications.
 */
export default function BusinessContactsPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ company_name: '', email: '', domain: '' });
  const [submitting, setSubmitting] = useState(false);

  const reload = async () => {
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/settings/business-contacts`, { credentials: 'include' });
      if (!res.ok) throw new Error('Could not load business contacts');
      const data = await res.json();
      setRows(data.contacts || []);
    } catch (e) {
      setError(e.message || 'Could not load.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name.trim() || (!form.email.trim() && !form.domain.trim())) {
      setError('Company name and either email or domain are required.');
      return;
    }
    setSubmitting(true); setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/settings/business-contacts`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Add failed');
      }
      setForm({ company_name: '', email: '', domain: '' });
      await reload();
    } catch (e2) {
      setError(e2.message || 'Could not add.');
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id, label) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove ${label || 'this contact'} from the whitelist?`)) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/settings/business-contacts/${id}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (!res.ok) throw new Error();
      await reload();
    } catch {
      // eslint-disable-next-line no-alert
      window.alert('Could not remove.');
    }
  };

  return (
    <div data-testid="business-contacts-panel" style={{ padding: '1rem 0' }}>
      <div style={{ marginBottom: 18 }}>
        <h2 style={{ margin: 0, fontSize: '1.35rem', color: '#0f172a' }}>
          Business Contacts Whitelist
        </h2>
        <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '0.92rem' }}>
          Emails from these senders bypass lead triage and AI auto-response. They appear
          in the dashboard's <strong>Business Emails</strong> section as notifications only —
          you reply manually. Defaults (Companies House, HMRC, Stripe, Resend, Gov.uk) are
          seeded automatically.
        </p>
      </div>

      <form onSubmit={submit}
            style={{ background: '#fff', border: '1px solid #e2e8f0',
                     borderRadius: 12, padding: '1rem 1.2rem', marginBottom: 16 }}>
        <h3 style={{ margin: '0 0 8px', fontSize: '1rem', color: '#0f172a' }}>
          Add a contact
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto',
                      gap: '0.7rem', alignItems: 'end' }}>
          <div>
            <label style={{ fontSize: '0.72rem', color: '#334155',
                            textTransform: 'uppercase', fontWeight: 600,
                            letterSpacing: '0.05em' }}>Company name</label>
            <input type="text" value={form.company_name}
                    onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))}
                    data-testid="bc-input-company"
                    placeholder="e.g. Acme Accounting"
                    style={{ width: '100%', padding: '0.5rem 0.65rem',
                             marginTop: 4, border: '1px solid #cbd5e1',
                             borderRadius: 6, fontSize: '0.9rem' }} />
          </div>
          <div>
            <label style={{ fontSize: '0.72rem', color: '#334155',
                            textTransform: 'uppercase', fontWeight: 600,
                            letterSpacing: '0.05em' }}>Email (optional)</label>
            <input type="email" value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    data-testid="bc-input-email"
                    placeholder="invoices@acme.co.uk"
                    style={{ width: '100%', padding: '0.5rem 0.65rem',
                             marginTop: 4, border: '1px solid #cbd5e1',
                             borderRadius: 6, fontSize: '0.9rem' }} />
          </div>
          <div>
            <label style={{ fontSize: '0.72rem', color: '#334155',
                            textTransform: 'uppercase', fontWeight: 600,
                            letterSpacing: '0.05em' }}>Or domain</label>
            <input type="text" value={form.domain}
                    onChange={e => setForm(f => ({ ...f, domain: e.target.value }))}
                    data-testid="bc-input-domain"
                    placeholder="acme.co.uk"
                    style={{ width: '100%', padding: '0.5rem 0.65rem',
                             marginTop: 4, border: '1px solid #cbd5e1',
                             borderRadius: 6, fontSize: '0.9rem' }} />
          </div>
          <button type="submit" disabled={submitting}
                  data-testid="bc-add-btn"
                  style={{ background: submitting ? '#94a3b8' : '#0073EA',
                           color: '#fff', border: 'none', borderRadius: 6,
                           padding: '0.55rem 1.2rem', fontWeight: 600,
                           cursor: submitting ? 'not-allowed' : 'pointer' }}>
            {submitting ? 'Adding…' : 'Add'}
          </button>
        </div>
        {error && (
          <div data-testid="bc-error"
               style={{ color: '#b91c1c', fontSize: '0.85rem', marginTop: 10 }}>
            {error}
          </div>
        )}
      </form>

      {loading ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>Loading…</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse',
                        background: '#fff', borderRadius: 12, overflow: 'hidden',
                        boxShadow: '0 1px 3px rgba(15,23,42,0.06)' }}
                data-testid="bc-table">
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
              <th style={{ textAlign: 'left', padding: '0.7rem 0.9rem',
                           color: '#475569', fontSize: '0.78rem',
                           textTransform: 'uppercase', letterSpacing: '0.05em' }}>Company</th>
              <th style={{ textAlign: 'left', padding: '0.7rem 0.9rem',
                           color: '#475569', fontSize: '0.78rem',
                           textTransform: 'uppercase', letterSpacing: '0.05em' }}>Email / Domain</th>
              <th style={{ width: 100, padding: '0.7rem 0.9rem',
                           textAlign: 'right' }}></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={3} style={{ padding: '1.5rem', textAlign: 'center', color: '#64748b' }}>
                No business contacts yet.
              </td></tr>
            ) : rows.map(r => (
              <tr key={r.id} data-testid={`bc-row-${r.id}`}
                  style={{ borderBottom: '1px solid #f1f5f9' }}>
                <td style={{ padding: '0.65rem 0.9rem', fontWeight: 600,
                              color: '#0f172a' }}>
                  {r.company_name}
                  {r.is_default && (
                    <span style={{ marginLeft: 8, background: '#eef2ff',
                                    color: '#4338ca', fontSize: '0.7rem',
                                    padding: '0.15rem 0.5rem', borderRadius: 999,
                                    fontWeight: 600 }}>
                      Default
                    </span>
                  )}
                </td>
                <td style={{ padding: '0.65rem 0.9rem', color: '#475569' }}>
                  {r.email ? r.email : ''}
                  {r.email && r.domain ? ' · ' : ''}
                  {r.domain ? `@${r.domain}` : ''}
                </td>
                <td style={{ padding: '0.65rem 0.9rem', textAlign: 'right' }}>
                  <button type="button" onClick={() => remove(r.id, r.company_name)}
                          data-testid={`bc-remove-${r.id}`}
                          style={{ background: '#fef2f2', color: '#b91c1c',
                                   border: '1px solid #fecaca', borderRadius: 6,
                                   padding: '0.3rem 0.75rem', fontWeight: 600,
                                   fontSize: '0.78rem', cursor: 'pointer' }}>
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
